import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/context/LanguageContext";
import { ShieldCheck, ArrowLeft, RefreshCw, Globe } from "lucide-react";
import { toast } from "sonner";

export default function VerifyOtp() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const email = params.get("email") || location.state?.email || "";
  
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(60);
  const [resending, setResending] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  // Timer for Resend OTP cooldown
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const verify = async (e) => {
    if (e) e.preventDefault();
    if (!otp || otp.length < 6) return toast.error(language === "hi" ? "6-अंकों का कोड दर्ज करें" : "Enter the 6-digit code");
    
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-email-otp", { email, otp });
      if (data.token) {
        localStorage.setItem("jjodi_token", data.token);
      }
      setUser(data.user);
      toast.success(language === "hi" ? "ईमेल सफलतापूर्वक सत्यापित! आपका स्वागत है।" : "Email verified successfully! Welcome.");
      const isStaff = data.user.role === "admin" || data.user.role === "mitra" || data.user.is_mitra;
      navigate((isStaff || data.user.profile_complete) ? "/app" : "/onboarding");
    } catch (e) {
      toast.error(formatError(e) || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    try {
      await api.post("/auth/resend-email-otp", { email });
      toast.success(language === "hi" ? "सत्यापन कोड सफलतापूर्वक दोबारा भेजा गया!" : "Verification code resent successfully!");
      setCooldown(60);
    } catch (e) {
      toast.error(formatError(e) || "Failed to resend code");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1595152772835-219674b2a8a6?auto=format&fit=crop&q=80&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-primary/40 to-transparent" />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <div className="inline-flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-secondary" />
            <span className="font-heading text-2xl">Hamarsathi</span>
          </div>
          <div>
            <h2 className="font-heading text-5xl leading-tight">
              {language === "hi" ? "अपना खाता सुरक्षित करें।" : "Secure your account."}
            </h2>
            <p className="mt-4 text-white/85 max-w-md">
              {language === "hi" ? "हम आपकी सुरक्षा और गोपनीयता को महत्व देते हैं। ओटीपी सत्यापन सुनिश्चित करता है कि आपका खाता सुरक्षित रहे।" : "We value your security and privacy. OTP verification ensures your account stays safe."}
            </p>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-white/70">
            {language === "hi" ? "100% सत्यापित सदस्य" : "100% Verified Members"}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 relative">
        <div className="absolute inset-0 mandala-bg pointer-events-none" />
        <div className="relative w-full max-w-md">
          <div className="flex justify-between items-center mb-6">
            <Link to="/register" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary">
              <ArrowLeft className="w-4 h-4 mr-1" />{language === "hi" ? "पंजीकरण पर वापस जाएं" : "Back to Register"}
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLanguage(language === "en" ? "hi" : "en")}
              className="rounded-full flex items-center gap-1.5 px-3 hover:bg-muted"
              data-testid="lang-switch-btn"
            >
              <Globe className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase">{language === "en" ? "हिं" : "EN"}</span>
            </Button>
          </div>
          <h1 className="font-heading text-4xl text-primary flex items-center gap-2">
            <ShieldCheck className="w-8 h-8 text-primary" /> {language === "hi" ? "ईमेल सत्यापित करें" : "Verify Email"}
          </h1>
          <p className="text-muted-foreground mt-2">
            {language === "hi" ? (
              <>हमने <span className="font-semibold text-primary">{email}</span> पर 6 अंकों का सत्यापन कोड भेजा है।</>
            ) : (
              <>We've sent a 6-digit verification code to <span className="font-semibold text-primary">{email}</span>.</>
            )}
          </p>

          <form onSubmit={verify} className="mt-8 space-y-6">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                {language === "hi" ? "सत्यापन कोड" : "Verification Code"}
              </Label>
              <Input
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                maxLength={6}
                placeholder="123456"
                inputMode="numeric"
                data-testid="email-otp-input"
                className="mt-1.5 text-center text-2xl tracking-[0.5em] font-mono h-14 border-primary/20 focus-visible:border-primary focus-visible:ring-primary/20"
              />
            </div>

            <Button type="submit" disabled={busy || otp.length < 6} className="w-full rounded-full h-11 btn-press" data-testid="email-otp-submit">
              {busy ? (language === "hi" ? "सत्यापित हो रहा है..." : "Verifying...") : (language === "hi" ? "सत्यापित करें और जारी रखें" : "Verify & Continue")}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">{language === "hi" ? "कोड प्राप्त नहीं हुआ? " : "Didn't receive code? "}</span>
            {cooldown > 0 ? (
              <span className="text-primary font-medium">
                {language === "hi" ? `${cooldown} सेकंड में पुनः भेजें` : `Resend in ${cooldown}s`}
              </span>
            ) : (
              <button
                onClick={handleResend}
                disabled={resending}
                className="text-primary font-medium hover:underline inline-flex items-center gap-1"
                data-testid="email-otp-resend"
              >
                {resending && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                {t("auth.resendOtp")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
